import { inject, Injectable } from '@angular/core';
import { PrintLabelComponent } from '../components/print-label.component';
import { PrintLabelsComponent } from '../components/print-labels.component';
import { PrintLabelOptions, PrintOptions } from '../models/print-options.model';
import { asArraySafe } from '@pf/shared-utility';
import { LabelType } from '../models/label-types.enum';
import { ItemForLabel, LabelTemplates } from '../models/label-templates';
import {
    ContainerDto,
    EquipmentDto,
    SlotDto,
    ZoneDto,
} from '@pf/platform-wms/domain';
import { DrawerService } from '@pf/shared-ui';
import { uniqBy } from 'lodash-es';
import { ZoneFragment } from '@control-tower/platform-wms';

@Injectable({
    providedIn: 'root',
})
export class LabelPrintService {
    private readonly drawerService = inject(DrawerService);

    printHandlingUnitLabels(equipment: EquipmentDto, autoPrint = false) {
        const handlingUnitOptions = asArraySafe(equipment.containers).map(
            container => this.mapContainerToLabelOptions(container)
        );
        this.printLabels({
            labelType: LabelType.HandlingUnit,
            autoPrint,
            labels: handlingUnitOptions,
            parentLabel: this.mapEquipmentToLabelOptions(equipment),
            widthInches: 3,
            heightInches: 1,
        });
    }

    printZoneLabels(zones: ZoneDto[], autoPrint = false) {
        const zoneLabelOptions = zones.map(zone =>
            this.mapZoneToLabelOptions(zone)
        );
        this.printLabels({
            labelType: LabelType.Zone,
            autoPrint,
            labels: zoneLabelOptions,
            widthInches: 3,
            heightInches: 1,
        });
    }

    printSlotLabels(slots: SlotDto[], autoPrint = false) {
        const uniqZones = uniqBy(
            slots.map(s => s.zone),
            'id'
        );
        const slotLabelOptions = slots.map(slot =>
            this.mapSlotToLabelOptions(slot)
        );
        this.printLabels({
            labelType: LabelType.Slot,
            autoPrint,
            labels: slotLabelOptions,
            parentLabel:
                uniqZones.length === 1
                    ? this.mapZoneToLabelOptions(uniqZones[0])
                    : undefined,
            widthInches: 3,
            heightInches: 1,
        });
    }

    printItemLabels(items: ItemForLabel[], autoPrint = false) {
        const itemLabelOptions = items.map(item =>
            this.mapItemToLabelOptions(item)
        );
        this.printLabels({
            labelType: LabelType.Item,
            autoPrint,
            labels: itemLabelOptions,
            widthInches: 3,
            heightInches: 1,
        });
    }

    printHandlingUnitLabel(container: ContainerDto, autoPrint = true) {
        this.printLabel({
            labelType: LabelType.HandlingUnit,
            autoPrint,
            labels: [this.mapContainerToLabelOptions(container)],
            widthInches: 3,
            heightInches: 1,
        });
    }

    printZoneLabel(zone: ZoneDto, autoPrint = true) {
        this.printLabel({
            labelType: LabelType.Zone,
            autoPrint,
            labels: [this.mapZoneToLabelOptions(zone)],
            widthInches: 3,
            heightInches: 1,
        });
    }

    printSlotLabel(slot: SlotDto, autoPrint = true) {
        this.printLabel({
            labelType: LabelType.Slot,
            autoPrint,
            labels: [this.mapSlotToLabelOptions(slot)],
            widthInches: 3,
            heightInches: 1,
        });
    }

    printItemLabel(item: ItemForLabel, autoPrint = true) {
        this.printLabel({
            labelType: LabelType.Item,
            autoPrint,
            labels: [this.mapItemToLabelOptions(item)],
            widthInches: 3,
            heightInches: 1,
        });
    }

    printLabel(printOptions: PrintOptions, onClose?: () => void) {
        this.drawerService.createTemplateDrawer({
            title: `Print Label`,
            content: PrintLabelComponent,
            onClose: onClose,
            size: 'large',
            isMobile: true,
            data: printOptions,
        });
    }

    printLabels(printOptions: PrintOptions, onClose?: () => void) {
        this.drawerService.createTemplateDrawer({
            title: `Print Label`,
            content: PrintLabelsComponent,
            onClose: onClose,
            size: 'large',
            isMobile: true,
            data: printOptions,
        });
    }

    private mapContainerToLabelOptions(
        container: ContainerDto
    ): PrintLabelOptions {
        return {
            barcode: container.lpn as string,
            tags: [container.containerType?.name as string],
            zpl: LabelTemplates[LabelType.HandlingUnit](
                container.lpn as string,
                container.containerType?.name as string
            ),
        };
    }

    private mapEquipmentToLabelOptions(
        equipment: EquipmentDto
    ): PrintLabelOptions {
        return {
            barcode: equipment.barcode as string,
            tags: [equipment.equipmentType?.name as string],
            zpl: LabelTemplates[LabelType.HandlingUnit](
                equipment.barcode as string,
                equipment.equipmentType.name as string
            ),
        };
    }

    private mapZoneToLabelOptions(zone: ZoneDto | ZoneFragment) {
        return {
            barcode: zone.barcode as string,
            tags: [],
            zpl: LabelTemplates[LabelType.Zone](zone),
        };
    }

    private mapSlotToLabelOptions(slot: SlotDto): PrintLabelOptions {
        return {
            barcode: slot.barcode as string,
            tags: [slot.slotType as string],
            zpl: LabelTemplates[LabelType.Slot](slot),
        };
    }

    private mapItemToLabelOptions(item: ItemForLabel): PrintLabelOptions {
        return {
            barcode: (item.sku || item.code) as string,
            tags: [],
            zpl: LabelTemplates[LabelType.Item](item),
        };
    }
}
